import { ItemTitle, ResponsiveImage } from '@sleepstation/components'
import { LinkBase } from '@sleepstation/components-base'
import { HLayout, VLayout } from '@sleepstation/components-layouts'
import { CopyRenderer } from '@sleepstation/components-markdown-renderer'
import { LAYOUT_SPACING, SIZE } from '@sleepstation/const'
import { parseToAst } from '@sleepstation/markdown'
import {
  backgroundColor,
  color,
  mq,
  padding,
  right,
} from '@sleepstation/style-api-helpers'
import { outset } from '@sleepstation/style-mixins'
import { reversedTheme } from '@sleepstation/themes-public-site'
import { isRunningInBrowser, rem } from '@sleepstation/utils'
import PropTypes from 'prop-types'
import { mergeDeepRight } from 'ramda'
import React from 'react'
import styled, { ThemeProvider, withTheme } from 'styled-components'

// -----------------------------------------------------------------------------
// Const
// -----------------------------------------------------------------------------

const IMAGE_ALT = `Illustration`

const IMAGE_ASPECT_RATIO = 66 / 78

// -----------------------------------------------------------------------------
// Utils
// -----------------------------------------------------------------------------

const isCurrentUrl = (path) => {
  if (!isRunningInBrowser()) return false
  return path === window.location.pathname
}

const modifyReversedTheme = () => {
  const goldDarkColor = reversedTheme.api.getColor(`goldDark`)
  const inlineLinkHighlight = { colors: { inlineLink: goldDarkColor } }

  return mergeDeepRight(reversedTheme, inlineLinkHighlight)
}

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------

const Title = styled(ItemTitle)`
  ${color(`inlineLink`)}
`

const Wrapper = styled(LinkBase)`
  text-decoration-line: none;
  height: 100%;
  display: block;
  cursor: pointer;
`

const Layout = styled(HLayout).attrs({
  spacing: [LAYOUT_SPACING.LARGE],
})`
  position: relative;
  height: 100%;
  ${padding(1)}

  ${backgroundColor(`background`)}

  ${mq(`smallUp`)} {
    ${outset(SIZE.LARGE, `background`)}
  }
`

const Primary = styled(VLayout).attrs({
  spacing: LAYOUT_SPACING.MEDIUM,
})`
  width: 77%;
`

const Secondary = styled.div`
  position: absolute;
  bottom: 0;
  ${right([0.5, 1])}
  width: ${rem(66)};

  ${mq(`smallUp`)} {
    width: ${rem(81)};
  }
`

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const MenuItem = ({
  title,
  body,
  url,
  imageUrl,
  onClick,
  isHighlighted,
  theme,
}) => {
  const bodyAst = parseToAst(body)
  const as = isCurrentUrl(url) ? `div` : `a`

  const resolvedTheme = isHighlighted ? modifyReversedTheme() : theme

  return (
    <Wrapper href={url} onClick={onClick} as={as}>
      <ThemeProvider theme={resolvedTheme}>
        <Layout>
          <Primary>
            <Title>{title}</Title>
            <CopyRenderer htmlAst={bodyAst} />
          </Primary>
          <Secondary>
            <ResponsiveImage
              aspectRatio={IMAGE_ASPECT_RATIO}
              src={imageUrl}
              alt={IMAGE_ALT}
            />
          </Secondary>
        </Layout>
      </ThemeProvider>
    </Wrapper>
  )
}

MenuItem.propTypes = {
  title: PropTypes.string.isRequired,
  body: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  imageUrl: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  theme: PropTypes.object.isRequired,
  isHighlighted: PropTypes.bool,
}

MenuItem.defaultProps = {
  isHighlighted: false,
}

export default withTheme(MenuItem)
